import JSZip from 'jszip';
import { SpecAssetFile } from '../types/spec';

export interface RawFileEntry {
  path: string;
  name: string;
  folderPath: string;
  content: string;
  isAsset?: boolean;
  mimeType?: string;
  dataUrl?: string;
}

export class FileSystemIngestion {
  /**
   * Reads a local directory via HTML5 File System Access API recursively
   */
  public static async readLocalDirectory(dirHandle: any): Promise<{ textFiles: RawFileEntry[]; assets: SpecAssetFile[] }> {
    const textFiles: RawFileEntry[] = [];
    const assets: SpecAssetFile[] = [];
    await this.traverseDirectory(dirHandle, '', textFiles, assets);
    return { textFiles, assets };
  }

  private static async traverseDirectory(dirHandle: any, currentPath: string, textFiles: RawFileEntry[], assets: SpecAssetFile[]): Promise<void> {
    for await (const entry of dirHandle.values()) {
      const entryPath = currentPath ? `${currentPath}/${entry.name}` : entry.name;
      const folderPath = currentPath || 'root';

      if (entry.kind === 'file') {
        const ext = entry.name.split('.').pop()?.toLowerCase() || '';

        if (['md', 'markdown', 'mdx', 'json', 'yaml', 'yml'].includes(ext)) {
          const file = await entry.getFile();
          const content = await file.text();
          textFiles.push({
            path: entryPath,
            name: entry.name,
            folderPath,
            content
          });
        } else if (['svg', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
          const file = await entry.getFile();
          const buffer = await file.arrayBuffer();
          const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
          const mimeType = ext === 'svg' ? 'image/svg+xml' : `image/${ext}`;
          const dataUrl = `data:${mimeType};base64,${base64}`;

          assets.push({
            path: entryPath,
            name: entry.name,
            folderPath,
            mimeType,
            dataUrl,
            isDiagram: true
          });
        }
      } else if (entry.kind === 'directory' && !entry.name.startsWith('.') && entry.name !== 'node_modules') {
        await this.traverseDirectory(entry, entryPath, textFiles, assets);
      }
    }
  }

  /**
   * Reads a .zip spec package file recursively preserving all subfolders and assets
   */
  public static async readZipPackage(file: File): Promise<{ textFiles: RawFileEntry[]; assets: SpecAssetFile[] }> {
    const zip = await JSZip.loadAsync(file);
    const textFiles: RawFileEntry[] = [];
    const assets: SpecAssetFile[] = [];

    for (const [filename, fileObj] of Object.entries(zip.files)) {
      if (fileObj.dir) continue;

      const pathParts = filename.split('/');
      const name = pathParts.pop() || filename;
      const folderPath = pathParts.join('/') || 'root';
      const ext = name.split('.').pop()?.toLowerCase() || '';

      if (['md', 'markdown', 'mdx', 'json', 'yaml', 'yml'].includes(ext)) {
        const content = await fileObj.async('string');
        textFiles.push({
          path: filename,
          name,
          folderPath,
          content
        });
      } else if (['svg', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
        const base64 = await fileObj.async('base64');
        const mimeType = ext === 'svg' ? 'image/svg+xml' : `image/${ext}`;
        const dataUrl = `data:${mimeType};base64,${base64}`;

        assets.push({
          path: filename,
          name,
          folderPath,
          mimeType,
          dataUrl,
          isDiagram: true
        });
      }
    }

    return { textFiles, assets };
  }

  /**
   * Parses raw pasted markdown content into a single document file entry
   */
  public static readPastedMarkdown(rawContent: string, title: string = 'Pasted-Spec.md'): RawFileEntry[] {
    return [
      {
        path: title,
        name: title,
        folderPath: 'root',
        content: rawContent
      }
    ];
  }
}
